import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdOutlineEdit, MdDeleteForever, MdDashboard } from "react-icons/md";
import { IoRefreshCircleOutline } from "react-icons/io5";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Dashboard.css";
import banner3 from "../NetZerobanner.png";
import { FaSignOutAlt } from "react-icons/fa";
import { getAllHotelDetails, deleteHotels, downloadHotelsExcel } from "../../ApiService";
import { useAuthStore } from "../../store/useAuthStore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const Dashboard = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);
  const [selectedHotels, setSelectedHotels] = useState([]);

  const rowsPerPage = 6;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { token, initializeAuth } = useAuthStore();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

 const {
  data: hotelData = [],
  refetch,
  isFetching,
  isLoading,
  isError,
} = useQuery({
  queryKey: ["hotels"],
  queryFn: getAllHotelDetails,
  refetchOnWindowFocus: false,
  staleTime: 0, // Always get fresh data on refetch
});

  // ✅ React Query for deleting hotel(s)
  const deleteMutation = useMutation({
  mutationFn: async (idsToDelete) => {
    await deleteHotels(idsToDelete, token);
  },
  onSuccess: async (_, idsToDelete) => {
  toast.success(`${idsToDelete.length} project(s) deleted successfully`);
  await queryClient.invalidateQueries({ queryKey: ["hotels"] });
  await queryClient.refetchQueries({ queryKey: ["hotels"], exact: true });
},
  onError: () => {
    toast.error("Failed to delete hotel(s). Please try again.", {
      position: "top-right",
      autoClose: 3000,
    });
  },
  onSettled: () => {
    setShowDeletePopup(false);
    setHotelToDelete(null);
    setSelectedHotels([]);
  },
});

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!sortConfig.key) return hotelData;
    return [...hotelData].sort((a, b) => {
      let valueA = a[sortConfig.key];
      let valueB = b[sortConfig.key];
      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "ascending" ? valueA - valueB : valueB - valueA;
      }
      valueA = String(valueA || "").toLowerCase();
      valueB = String(valueB || "").toLowerCase();
      if (valueA < valueB) return sortConfig.direction === "ascending" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon active">{sortConfig.direction === "ascending" ? "↑" : "↓"}</span>;
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = getSortedData().slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(hotelData.length / rowsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleModify = (hotelId) => {
    navigate(`/project-details/${hotelId}`, {
    state: { _adminEdit: true }
  });
  };


  const handleNewProject = () => {
    navigate("/project-details");
  };

  const handleSelectHotel = (hotelId) => {
    setSelectedHotels((prev) =>
      prev.includes(hotelId) ? prev.filter((id) => id !== hotelId) : [...prev, hotelId]
    );
  };

  const handleBulkDeleteClick = () => {
    if (selectedHotels.length === 0) {
      toast.warning("Please select projects to delete");
      return;
    }
    setHotelToDelete(selectedHotels);
    setShowDeletePopup(true);
  };

  const handleDelete = () => {
    const idsToDelete = Array.isArray(hotelToDelete) ? hotelToDelete : [hotelToDelete];
    deleteMutation.mutate(idsToDelete);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("User");
    localStorage.setItem("logoutEvent", Date.now());
    navigate("/");
  };

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
    setHotelToDelete(null);
  };

  const handleDownloadExcel = async () => {
    try {
      const url = downloadHotelsExcel(selectedHotels);
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error("Failed to download Excel");
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download =
        selectedHotels.length > 0 ? "Selected_Hotel_NetZero_Assessment.xlsx" : "Hotel_NetZero_Assessment.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(
        selectedHotels.length > 0
          ? "Selected hotel(s) Excel downloaded successfully!"
          : "All hotels Excel downloaded successfully!",
        { position: "top-right", autoClose: 3000 }
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="dashboard-wrapper">
      <img src={banner3} alt="Raddison Logo" className="company-logo-d" />
      <div className="dashboard-header">
        <div className="header-title">
          <h1>
            <MdDashboard /> Dashboard
          </h1>
        </div>

        <div className="header-actions">
          <button className="icon-button" title="Delete Selected" onClick={handleBulkDeleteClick}>
            <MdDeleteForever />
          </button>
        <button
  className={`icon-button ${isFetching ? "refreshing" : ""}`}
  title="Refresh"
  onClick={async () => {
    await queryClient.refetchQueries({ queryKey: ["hotels"], exact: true });
    toast.success("Data refreshed successfully!", {
      position: "top-right",
      autoClose: 1000,
    });
  }}
  disabled={isFetching}
>
  <IoRefreshCircleOutline />
</button>
          <button className="new-project-btn" onClick={handleNewProject}>
            New Project +
          </button>
          <button className="excel-download-btn" onClick={handleDownloadExcel}>
            Download Excel
          </button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-wrapper">
          <table className="certification-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="custom-checkbox"
                    checked={selectedHotels.length === currentRows.length}
                    onChange={() => {
                      if (selectedHotels.length === currentRows.length) {
                        setSelectedHotels([]);
                      } else {
                        setSelectedHotels(currentRows.map((hotel) => hotel.hotelId));
                      }
                    }}
                  />
                </th>
                <th onClick={() => handleSort("hotelName")}>
                  Hotel Name {getSortIcon("hotelName")}
                </th>
                <th onClick={() => handleSort("country")}>Country {getSortIcon("country")}</th>
                <th onClick={() => handleSort("sitaCode")}>SITA Code {getSortIcon("sitaCode")}</th>
                <th onClick={() => handleSort("readinessLevels")}>
                  Readiness {getSortIcon("readinessLevels")}
                </th>
                <th onClick={() => handleSort("totalPoints")}>Points/28 {getSortIcon("totalPoints")}</th>
                <th onClick={() => handleSort("contractType")}>
                  Contract Type {getSortIcon("contractType")}
                </th>
                <th onClick={() => handleSort("managerName")}>
                  Manager {getSortIcon("managerName")}
                </th>
                <th>RHG Email {getSortIcon("managerEmail")}</th>
                <th>Job Title {getSortIcon("jobTitle")}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((hotel, index) => (
                <tr key={index}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedHotels.includes(hotel.hotelId)}
                      onChange={() => handleSelectHotel(hotel.hotelId)}
                    />
                  </td>
                  <td>{hotel.hotelName}</td>
                  <td>{hotel.country}</td>
                  <td>{hotel.sitaCode}</td>
                  <td
                    className={
                      hotel.readinessLevels === "Hotel not ready"
                        ? "readiness-not-ready"
                        : hotel.readinessLevels === "Hotel fairly ready"
                        ? "readiness-fairly-ready"
                        : hotel.readinessLevels === "Hotel ready"
                        ? "readiness-ready"
                        : ""
                    }
                  >
                    {hotel.readinessLevels}
                  </td>
                  <td>{hotel.totalPoints}</td>
                  <td>{hotel.contractType}</td>
                  <td>{hotel.managerName}</td>
                  <td>{hotel.managerEmail}</td>
                  <td>{hotel.jobTitle}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="icon-button modify-btn"
                        title="Edit"
                        onClick={() => handleModify(hotel.hotelId)}
                      >
                        <MdOutlineEdit size={20} />
                      </button>
                      <button
                        className="icon-button delete-btn"
                        title="Delete"
                        onClick={() => {
                          setHotelToDelete(hotel.hotelId);
                          setShowDeletePopup(true);
                        }}
                      >
                        <MdDeleteForever size={20} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button className="pagination-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>
          <div className="pagination-numbers">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`pagination-btn ${currentPage === i + 1 ? "active" : ""}`}
                onClick={() => paginate(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button
            className="pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {showDeletePopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h3>Confirm Deletion</h3>
            <p>
              {Array.isArray(hotelToDelete)
                ? `Are you sure you want to delete ${hotelToDelete.length} selected project(s)?`
                : "Are you sure you want to delete this Project?"}
            </p>
            <div className="popup-buttons">
              <button className="btn-delete" onClick={handleDelete}>
                Yes
              </button>
              <button className="btn-cancel" onClick={closeDeletePopup}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />

      <button className="logout-btn" onClick={handleLogout}>
        <FaSignOutAlt className="logout-icon" /> Logout
      </button>
    </div>
  );
};

export default Dashboard;